// What an EFP confirmation turns into: the positions to write, the basis position to
// reduce, and anything that should stop the user before any of it is sent.
//
// Pure, so the whole posting can be asserted without an API. The submitter does the I/O.
//
// THE SHAPE OF AN EFP HERE
//
// The hedge for a plant is carried as long futures at the plant's PARENT location. When
// a purchase confirmation arrives, that hedge moves to the plant and is delivered
// against the physical:
//
//   parent   SELL futures  @ weighted avg of the parent's long futures   (hedge closed)
//   plant    BUY  futures  @ the same weighted average                   (hedge moved in)
//   plant    SELL futures  @ the same weighted average                   (EFP delivery)
//   plant    BUY  physical @ the contract's BOARD price
//   plant    BUY  basis    @ the contract's basis
//
// All four futures legs carry the weighted average, so the futures book nets flat at the
// plant and the economics land on the physical and basis legs. The board price is used
// by the physical leg only - "the price should come from the contract, this is the board
// price and not the basis".
//
// Everything except the parent leg is allocated to the allocation group matching the
// contract's location, counterparty and commodity.
import {
    AllocationModel,
    CommodityModel,
    ContractDate,
    CounterpartyModel,
    Currency,
    LocationModel,
    PositionModel,
    PositionType,
    Side,
    Unit,
} from '../../../../Generated/Commodity-Management-Api';
import { calculateWeightedPrice } from '../../../../Helpers/PositionAggregationHelper';
import { filterBuyPositions } from '../../../../Helpers/PositionFilterHelper';
import { convertPositionToUnit } from '../../../../Helpers/UnitConversionHelper';
import { canRestatePrice, findBushelWeight, restatePrice } from './EfpUnits';

/**
 * Prices are compared at cent precision.
 *
 * A stored basis converted out of another unit lands on a long tail -
 * 94.48382657142858 against a contract's 94.48 - and comparing those raw reports a
 * mismatch on a contract that agrees to the cent. Both sides are rounded before the
 * comparison rather than given a tolerance, so the check means "these are the same price
 * as written".
 */
function toCents(value: number): number {
    return Math.round(value * 100) / 100;
}

/**
 * Volumes are whole numbers.
 *
 * A position converted out of another unit lands on a long tail - 845 tonnes of corn is
 * 33,266.699... bushels - and neither the book nor a confirmation deals in fractions of a
 * bushel. Converted volumes are rounded once, on the way in, so every comparison after
 * that is between whole numbers.
 */
function toWholeVolume(value: number | null | undefined): number {
    return Math.round(value ?? 0);
}

/**
 * How far two volumes may differ and still count as the same.
 *
 * Rounding each position independently can leave a one-unit gap between a position and
 * the contract that exactly covers it. Reporting that gap as a shortfall, or leaving a
 * single bushel behind on a basis position the contract consumes entirely, is noise that
 * trains people to ignore the warnings that matter.
 */
const VOLUME_TOLERANCE = 1;

/** Whether `held` falls short of `required` by more than rounding explains. */
function isShortBy(held: number, required: number): boolean {
    return required - held > VOLUME_TOLERANCE;
}

/** A remainder within the tolerance of zero IS zero; anything larger is real. */
function toZeroWithinTolerance(value: number): number {
    return Math.abs(value) <= VOLUME_TOLERANCE ? 0 : toWholeVolume(value);
}

/**
 * Which basis the contract is booked at when the confirmation and the book disagree.
 * Unset means the user has not chosen yet, and a disagreement blocks.
 */
export type EfpBasisSource = 'contract' | 'existing';

export interface EfpPlanInput {
    commodity: CommodityModel;
    counterparty: CounterpartyModel;
    /** The plant named on the contract. */
    location: LocationModel;
    /** Where the hedge is carried - location.parentLocationId resolved. */
    parentLocation?: LocationModel;
    /** Allocation group matching location + counterparty + commodity. */
    allocationGroup?: AllocationModel;
    volume: number;
    unit: Unit;
    currency: Currency;
    boardPrice: number;
    /** What boardPrice is per. Undefined means the contract's unit. */
    boardPriceUnit?: Unit;
    basisPrice: number;
    /** What basisPrice is per. Undefined means the contract's unit. */
    basisPriceUnit?: Unit;
    /** Futures month - the contract date on every leg. */
    futuresMonth: ContractDate;
    /** Delivery month - the budget date on every leg. */
    deliveryMonth: ContractDate;
    tradeDate: string;
    dealTicketNumber?: string;
    /** Everything currently on the books, for the futures average and the basis leg. */
    existingPositions: readonly PositionModel[];
    /** Resolves a basis mismatch. Ignored when the two agree. */
    basisSource?: EfpBasisSource;
}

export interface EfpLeg {
    /** Which location the leg is written at. */
    locationId?: number;
    positionType: PositionType;
    side: Side;
    volume: number;
    price: number;
    unit: Unit;
    currency: Currency;
    budgetDate: ContractDate;
    contractDate: ContractDate;
    /** Allocated to the group. The parent hedge-closing leg is not. */
    allocate: boolean;
    /** For the review screen. */
    description: string;
}

export interface EfpBasisReduction {
    positionId: number;
    /**
     * The ticket the position sits on. Updating a position means re-sending its whole
     * deal ticket: UpdateDealTicketService hard-deletes every position on the ticket that
     * the request leaves out, so the siblings have to be sent back untouched.
     */
    dealTicketId: number;
    /** Volume before the contract is applied. */
    currentVolume: number;
    /** What it becomes: currentVolume - contract volume, never below zero. */
    newVolume: number;
    price: number;
}

/** The contract's basis and the book's disagree; the user picks which one is booked. */
export interface EfpBasisMismatch {
    contractBasis: number;
    existingBasis: number;
    /** What was chosen, if anything. */
    source?: EfpBasisSource;
}

export interface EfpPlan {
    legs: EfpLeg[];
    /** Set when an existing basis position is being drawn down. */
    basisReduction?: EfpBasisReduction;
    /** Set when the contract's basis and the existing position's differ. */
    basisMismatch?: EfpBasisMismatch;
    /** Weighted average of the parent's long futures - the price on all futures legs. */
    futuresWeightedAveragePrice: number;
    /** Open long volume available at the parent. */
    parentOpenFuturesVolume: number;
    /** Blocking - the plan should not be submitted. */
    errors: string[];
    /** Worth showing, not blocking. */
    warnings: string[];
}

function sumVolumeOf(positions: readonly PositionModel[]): number {
    return positions.reduce((total, position) => total + (position.volume ?? 0), 0);
}

/**
 * The parent's long futures for this commodity and futures month, and their weighted
 * average price. Sells at the parent reduce the open volume but not the average - the
 * average is what the remaining longs cost.
 */
export function getParentFuturesPosition(
    existingPositions: readonly PositionModel[],
    commodityId: number,
    parentLocationId: number | undefined,
    futuresMonth: ContractDate
): { positions: PositionModel[]; weightedAveragePrice: number; openVolume: number } {
    const futures = existingPositions.filter(position =>
        position.positionType === PositionType.Future
        && position.commodity?.commodityId === commodityId
        && position.location?.locationId === parentLocationId
        && position.contractDate?.month === futuresMonth.month
        && position.contractDate?.year === futuresMonth.year);

    const buys = filterBuyPositions(futures);
    const sells = futures.filter(position => position.side === Side.Sell);

    return {
        positions: futures,
        weightedAveragePrice: calculateWeightedPrice(buys),
        openVolume: sumVolumeOf(buys) - sumVolumeOf(sells),
    };
}

/**
 * The basis position the contract draws down: same location and commodity, matched on
 * the DELIVERY month rather than the futures month.
 */
export function findExistingBasisPosition(
    existingPositions: readonly PositionModel[],
    commodityId: number,
    locationId: number | undefined,
    deliveryMonth: ContractDate
): PositionModel | undefined {
    return existingPositions.find(position =>
        position.positionType === PositionType.Basis
        && position.side === Side.Buy
        && position.commodity?.commodityId === commodityId
        && position.location?.locationId === locationId
        && position.budgetDate?.month === deliveryMonth.month
        && position.budgetDate?.year === deliveryMonth.year);
}

export function buildEfpPlan(input: EfpPlanInput): EfpPlan {
    const errors: string[] = [];
    const warnings: string[] = [];

    const {
        commodity, location, parentLocation, allocationGroup,
        volume, unit, currency,
        futuresMonth, deliveryMonth,
    } = input;

    // Everything is compared in the contract's unit. The book is rarely in that unit -
    // futures are often held in Contract lots and a basis position in bushels against a
    // tonne contract - and comparing those numbers raw comes out by a factor of 39 or
    // 5,000. Each position converts using its own pricing strategy's bushel weight and
    // lot size, so the conversion is per position rather than one blanket factor.
    const existingPositions = input.existingPositions.map(position => {
        const converted = convertPositionToUnit(position, unit);

        return {
            ...converted,
            volume: toWholeVolume(converted.volume),
            effectiveVolume: toWholeVolume(converted.effectiveVolume),
        };
    });

    // The board price on these forms is routinely a per-bushel futures quote on a tonne
    // contract, and the basis per tonne. Each is restated in the contract's unit before
    // anything is priced off it. The bushel weight is the commodity's, carried on the
    // pricing strategy of whatever position already holds it.
    const bushelWeight = findBushelWeight(input.existingPositions, commodity.commodityId);

    const toContractUnit = (label: string, value: number, from: Unit | undefined): number => {
        if (!from || from === unit) return value;

        if (!canRestatePrice(from, unit, bushelWeight)) {
            errors.push(
                `${label} is quoted per ${from} and cannot be restated per ${unit}: `
                + `no bushel weight is known for ${commodity.commodityName}`
            );
            return value;
        }

        const converted = restatePrice(value, from, unit, bushelWeight);
        warnings.push(`${label} ${value} per ${from} restated as ${converted} per ${unit}`);
        return converted;
    };

    const boardPrice = toContractUnit('Board price', input.boardPrice, input.boardPriceUnit);
    const basisPrice = toContractUnit('Basis', input.basisPrice, input.basisPriceUnit);

    if (!parentLocation) {
        errors.push(`${location.locationName} has no parent location, so there is no hedge to transfer from`);
    }

    if (!allocationGroup) {
        errors.push(`No allocation group for ${location.locationName} / ${input.counterparty.counterpartyName} / ${commodity.commodityName}`);
    }

    const parentFutures = getParentFuturesPosition(
        existingPositions, commodity.commodityId, parentLocation?.locationId, futuresMonth
    );

    if (parentFutures.positions.length === 0) {
        errors.push(
            `No futures at ${parentLocation?.locationName ?? 'the parent location'} for ${commodity.commodityName} `
            + `${futuresMonth.month}/${futuresMonth.year} to transfer`
        );
    } else if (isShortBy(parentFutures.openVolume, volume)) {
        // Not blocking: the hedge can legitimately be short of the contract and topped up
        // afterwards, but the user should see it before posting.
        warnings.push(
            `Only ${parentFutures.openVolume.toLocaleString()} ${unit} open at `
            + `${parentLocation?.locationName} against a contract of ${volume.toLocaleString()} ${unit}`
        );
    }

    const price = parentFutures.weightedAveragePrice;
    const existingBasis = findExistingBasisPosition(
        existingPositions, commodity.commodityId, location.locationId, deliveryMonth
    );

    // The confirmation's basis and the basis already on the books should agree - they
    // are the same number from two sources, and silently picking one hides a bad
    // contract or a stale position. When they differ the user says which is right;
    // until they do, the plan blocks.
    let basisMismatch: EfpBasisMismatch | undefined;
    let basisLegPrice = basisPrice;

    if (existingBasis && toCents(existingBasis.price ?? 0) !== toCents(basisPrice)) {
        const existingBasisPrice = existingBasis.price ?? 0;
        basisMismatch = { contractBasis: basisPrice, existingBasis: existingBasisPrice, source: input.basisSource };

        const where = `the ${deliveryMonth.month}/${deliveryMonth.year} basis position at ${location.locationName}`;

        if (input.basisSource === 'existing') {
            basisLegPrice = existingBasisPrice;
            warnings.push(
                `Basis booked at ${toCents(existingBasisPrice)} from ${where}, `
                + `not the contract's ${toCents(basisPrice)}`
            );
        } else if (input.basisSource === 'contract') {
            warnings.push(
                `Basis booked at the contract's ${toCents(basisPrice)}; ${where} `
                + `stays at ${toCents(existingBasisPrice)}`
            );
        } else {
            errors.push(
                `Basis mismatch: ${where} is ${toCents(existingBasisPrice)}, `
                + `the contract is ${toCents(basisPrice)} - choose which basis to use`
            );
        }
    }

    if (existingBasis && isShortBy(existingBasis.volume ?? 0, volume)) {
        warnings.push(
            `The basis position at ${location.locationName} holds `
            + `${(existingBasis.volume ?? 0).toLocaleString()} ${unit}, less than the contract's `
            + `${volume.toLocaleString()} ${unit}`
        );
    }

    if (!existingBasis) {
        warnings.push(
            `No existing basis position at ${location.locationName} for `
            + `${deliveryMonth.month}/${deliveryMonth.year} - one will be created and nothing reduced`
        );
    }

    const common = {
        volume,
        unit,
        currency,
        budgetDate: deliveryMonth,
        contractDate: futuresMonth,
    };

    const legs: EfpLeg[] = [
        {
            ...common,
            locationId: parentLocation?.locationId,
            positionType: PositionType.Future,
            side: Side.Sell,
            price,
            allocate: false,
            description: `Sell futures at ${parentLocation?.locationName ?? 'parent'} @ ${price} (closes the hedge)`,
        },
        {
            ...common,
            locationId: location.locationId,
            positionType: PositionType.Future,
            side: Side.Buy,
            price,
            allocate: true,
            description: `Buy futures at ${location.locationName} @ ${price} (hedge moved in)`,
        },
        {
            ...common,
            locationId: location.locationId,
            positionType: PositionType.Future,
            side: Side.Sell,
            price,
            allocate: true,
            description: `Sell futures at ${location.locationName} @ ${price} (EFP delivery)`,
        },
        {
            ...common,
            locationId: location.locationId,
            positionType: PositionType.Physical,
            side: Side.Buy,
            price: boardPrice,
            allocate: true,
            description: `Buy physical at ${location.locationName} @ ${boardPrice} (board price)`,
        },
        {
            ...common,
            locationId: location.locationId,
            positionType: PositionType.Basis,
            side: Side.Buy,
            price: basisLegPrice,
            allocate: true,
            description: `Buy basis at ${location.locationName} @ ${basisLegPrice}`,
        },
    ];

    const basisReduction: EfpBasisReduction | undefined = existingBasis
        ? {
            positionId: existingBasis.positionId!,
            dealTicketId: existingBasis.dealTicketId!,
            currentVolume: existingBasis.volume ?? 0,
            // A remainder inside the tolerance is rounding, not stock. Leaving it behind
            // would keep a one-bushel basis position open for a contract that consumed
            // the whole thing. A contract larger than the position takes all of it and
            // no more - a position cannot hold a negative volume.
            newVolume: Math.max(0, toZeroWithinTolerance((existingBasis.volume ?? 0) - volume)),
            price: existingBasis.price ?? 0,
        }
        : undefined;

    return {
        legs,
        basisReduction,
        basisMismatch,
        futuresWeightedAveragePrice: price,
        parentOpenFuturesVolume: parentFutures.openVolume,
        errors,
        warnings,
    };
}
